import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { analyticsConfig } from '@app/analytics-config';
import { environment } from '@env/environment';

declare global {
  interface Window {
    mixpanel: any;
    dataLayer: any;
    gtag: any;
  }
}

export interface AnalyticsEvent {
  name: string;
  enabled: boolean;
  providers: string[];
}

export interface AnalyticsSection {
  sectionName: string;
  status: 'enabled' | 'disabled' | 'inherit';
  events: AnalyticsEvent[];
}

export interface AnalyticsConfig {
  sections: AnalyticsSection[];
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private config: AnalyticsConfig | null = null;
  private configLoaded$ = new BehaviorSubject<boolean>(false);
  private analyticsEnabled = true;

  constructor(private http: HttpClient) {
    // Load analytics configuration on service initialization
    this.loadConfig();
  }

  /**
   * Load analytics configuration from the server
   */
  private loadConfig(): void {
    const headers = new HttpHeaders().set('X-Skip-Prefix-Interceptor', 'true');

    // this.http.get<AnalyticsConfig>('assets/analytics-config.json', { headers })
      of(analyticsConfig)
      .pipe(
        tap(config => {
          this.config = config as any;
          this.configLoaded$.next(true);
          console.log('Analytics configuration loaded successfully');
        }),
        catchError(error => {
          console.error('Error loading analytics configuration', error);
          this.configLoaded$.next(false);
          return of(null);
        })
      )
      .subscribe();
  }

  private gtag = function(...args: any[]){
    window.dataLayer.push(arguments);
  }

  initializeGoogleAnalytics(): void {
    window.dataLayer = window.dataLayer || [];
    this.gtag('js', new Date());
    this.gtag('config', environment.gaKey);
  }


  /**
   * Track an event across configured analytics providers
   *
   * @param sectionName The section the event belongs to
   * @param eventName The name of the event to track
   * @param properties Optional properties to include with the event
   */
  public trackEvent(sectionName: string, eventName: string, properties: Record<string, any> = {}): void {
    if (!this.analyticsEnabled || !this.config) {
      console.log(`Analytics disabled or config not loaded. Skipping event: ${eventName}`);
      return;
    }

    // Find the section
    const section = this.config.sections.find(s => s.sectionName === sectionName);
    if (!section) {
      console.warn(`Section not found: ${sectionName}. Skipping event: ${eventName}`);
      return;
    }

    // Find the event
    const event = section.events.find(e => e.name === eventName);
    if (!event) {
      console.warn(`Event not found: ${eventName} in section: ${sectionName}`);
      return;
    }

    // Check if the event should be tracked based on section and event status
    const shouldTrack = this.shouldTrackEvent(section, event);
    if (!shouldTrack) {
      console.log(`Event tracking disabled for: ${eventName} in section: ${sectionName}`);
      return;
    }

    // Track the event through each configured provider
    this.trackThroughProviders(event, properties);
  }

  /**
   * Determine if an event should be tracked based on section and event status
   */
  private shouldTrackEvent(section: AnalyticsSection, event: AnalyticsEvent): boolean {
    switch (section.status) {
      case 'disabled':
        return false;
      case 'enabled':
        return true;
      case 'inherit':
      default:
        return event.enabled;
    }
  }

  /**
   * Track the event through each configured provider
   */
  private trackThroughProviders(event: AnalyticsEvent, properties: Record<string, any>): void {
    const enhancedProperties = {
      ...properties,
      timestamp: new Date().toISOString()
    };
    for (const provider of event.providers) {
      switch (provider) {
        case 'MixPanel':
          this.trackMixpanel(event.name, enhancedProperties);
          break;
        case 'Google Analytics':
          this.trackGoogleAnalytics(event.name, enhancedProperties);
          break;
        default:
          console.warn(`Unknown analytics provider: ${provider}`);
      }
    }
  }

  /**
   * Track an event through Mixpanel
   */
  private trackMixpanel(eventName: string, properties: Record<string, any>): void {
    if (window.mixpanel && typeof window.mixpanel.track === 'function') {
      window.mixpanel.track(eventName, properties);
      console.log(`Mixpanel event tracked: ${eventName}`, properties);
    } else {
      console.warn('Mixpanel not available');
    }
  }

  /**
   * Track an event through Google Analytics
   */
  private trackGoogleAnalytics(eventName: string, properties: Record<string, any>): void {
    if (this.gtag) {
      this.gtag('event', eventName, properties);
      console.log(`Google Analytics event tracked: ${eventName}`, properties);
    } else {
      console.warn('Google Analytics not available');
    }
  }

  /**
   * Enable or disable analytics tracking
   */
  public setAnalyticsEnabled(enabled: boolean): void {
    this.analyticsEnabled = enabled;
    console.log(`Analytics tracking ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if the analytics configuration has been loaded
   */
  public isConfigLoaded(): Observable<boolean> {
    return this.configLoaded$.asObservable();
  }

  /**
   * Identify a user in Mixpanel using their email address.
   * This should be called when user email is captured (e.g., on personal info page)
   * or when user logs in.
   * 
   * @param email User's email address to use as the distinct ID
   * @param userProperties Optional additional properties to set on the user profile
   */
  public identifyUser(email: string, userProperties: Record<string, any> = {}): void {
    if (!email) {
      console.warn('Cannot identify user without email');
      return;
    }

    const normalizedEmail = email.toLowerCase().trim();

    if (window.mixpanel && typeof window.mixpanel.identify === 'function') {
      // Identify the user with their email as the distinct ID
      window.mixpanel.identify(normalizedEmail);

      // Set user profile properties
      const profileProperties = {
        $email: normalizedEmail,
        ...userProperties
      };

      if (typeof window.mixpanel.people?.set === 'function') {
        window.mixpanel.people.set(profileProperties);
      }

      console.log(`Mixpanel user identified: ${normalizedEmail}`, profileProperties);
    } else {
      console.warn('Mixpanel not available for user identification');
    }
  }

  /**
   * Set additional user profile properties in Mixpanel.
   * Can be called to update user properties after initial identification.
   * 
   * @param properties Properties to set on the user profile
   */
  public setUserProperties(properties: Record<string, any>): void {
    if (window.mixpanel && typeof window.mixpanel.people?.set === 'function') {
      window.mixpanel.people.set(properties);
      console.log('Mixpanel user properties updated', properties);
    } else {
      console.warn('Mixpanel not available for setting user properties');
    }
  }

  /**
   * Set user profile properties only if they haven't been set before.
   * Useful for setting initial values that shouldn't be overwritten.
   * 
   * @param properties Properties to set once on the user profile
   */
  public setUserPropertiesOnce(properties: Record<string, any>): void {
    if (window.mixpanel && typeof window.mixpanel.people?.set_once === 'function') {
      window.mixpanel.people.set_once(properties);
      console.log('Mixpanel user properties set once', properties);
    } else {
      console.warn('Mixpanel not available for setting user properties once');
    }
  }

  /**
   * Reset the Mixpanel identity.
   * Should be called on user logout to ensure subsequent events are not associated
   * with the previous user.
   */
  public resetIdentity(): void {
    if (window.mixpanel && typeof window.mixpanel.reset === 'function') {
      window.mixpanel.reset();
      console.log('Mixpanel identity reset');
    } else {
      console.warn('Mixpanel not available for identity reset');
    }
  }

  /**
   * Alias the current anonymous ID to a user ID.
   * This links pre-identification events to the identified user.
   * Should only be called once per user, typically on first identification.
   * 
   * @param userId The user ID to alias to (usually email)
   */
  public aliasUser(userId: string): void {
    if (!userId) {
      console.warn('Cannot alias user without userId');
      return;
    }

    const normalizedUserId = userId.toLowerCase().trim();

    if (window.mixpanel && typeof window.mixpanel.alias === 'function') {
      window.mixpanel.alias(normalizedUserId);
      console.log(`Mixpanel user aliased: ${normalizedUserId}`);
    } else {
      console.warn('Mixpanel not available for user aliasing');
    }
  }
}
